import type { InlineConfig } from 'vite';
import { build as viteBuild } from 'vite';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isType } from 'variant';
import { getDistFolder } from '@wix/cli-app-definitions';
import { rm } from 'node:fs/promises';
import type { ProjectModel } from './model.js';
import type { ViteBuildOutput } from './app-manifest/build-output-data-extractor.js';
import {
  getBackendViteConfig,
  getBuildWorkerViteConfig,
  getBuildBrowserViteConfig,
  getBuildSiteComponentsClientViteConfig,
  getBuildSiteComponentsSdkViteConfig,
  getBuildSiteComponentsServerViteConfig,
} from './vite/index.js';
import { writeAppManifestFile } from './app-manifest/file.js';
import { createAppManifest } from './app-manifest/create.js';
import { getComponentBuildSourceBuilder } from './app-manifest/component-source.js';
import { validateComponents } from './app-manifest/validate.js';
import { DevCenterComponent } from './dev-center/components/dev-center-component.js';
import type { Logger } from './vite/logger.js';
import { createLogger } from './vite/logger.js';

async function runViteBuild(config: InlineConfig) {
  try {
    return await viteBuild(config);
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.FailedToBuildApp(),
      cause: error,
    });
  }
}

function runBackendBuild(projectModel: ProjectModel, customLogger: Logger) {
  const backendComponents = projectModel.components.filter(
    (component) =>
      isType(component, DevCenterComponent.Api) ||
      isType(component, DevCenterComponent.WebMethod) ||
      isType(component, DevCenterComponent.Event) ||
      isType(component, DevCenterComponent.ServicePlugin)
  );

  if (backendComponents.length === 0) {
    return;
  }

  return runViteBuild(
    getBackendViteConfig(
      projectModel,
      { customLogger },
      {
        appSecret: '__APP_SECRET__',
        webhookPublicKey: '__WEBHOOK_PUBLIC_KEY__',
      }
    )
  );
}

function runWorkerBuild(projectModel: ProjectModel, customLogger: Logger) {
  const workerComponents = projectModel.components.filter(
    isType(DevCenterComponent.BlocksWidget)
  );

  if (workerComponents.length === 0) {
    return;
  }

  return runViteBuild(getBuildWorkerViteConfig(projectModel, { customLogger }));
}

function runSiteComponentsClientBuild(
  projectModel: ProjectModel,
  customLogger: Logger
) {
  const components = projectModel.components.filter(
    isType(DevCenterComponent.SiteComponent)
  );

  if (components.length === 0) {
    return;
  }

  return runViteBuild(
    getBuildSiteComponentsClientViteConfig(projectModel, { customLogger })
  );
}

async function runSiteComponentsServerBuild(
  projectModel: ProjectModel,
  customLogger: Logger
) {
  const components = projectModel.components.filter(
    isType(DevCenterComponent.SiteComponent)
  );

  if (components.length === 0) {
    return;
  }

  const output: ViteBuildOutput = [];
  const configs = getBuildSiteComponentsServerViteConfig(projectModel, {
    customLogger,
  });

  for (const config of configs) {
    const result = await runViteBuild(config);

    if (Array.isArray(result)) {
      output.push(...result);
    } else if ('output' in result) {
      output.push(result);
    }
  }

  return output;
}

async function runSiteComponentsSdkBuild(
  projectModel: ProjectModel,
  customLogger: Logger
) {
  const components = projectModel.components.filter(
    isType(DevCenterComponent.SiteComponent)
  );

  if (components.length === 0) {
    return;
  }

  const output: ViteBuildOutput = [];
  const configs = getBuildSiteComponentsSdkViteConfig(projectModel, {
    customLogger,
  });

  for (const config of configs) {
    const result = await runViteBuild(config);

    if (Array.isArray(result)) {
      output.push(...result);
    } else if ('output' in result) {
      output.push(result);
    }
  }

  return output;
}

export async function build(projectModel: ProjectModel) {
  // Validate before building the project, to fail fast in case of a validation issue
  if (projectModel.components.length === 0) {
    throw new CliError({
      code: CliErrorCode.NoExtensionsFound(),
      cause: null,
    });
  }

  validateComponents(projectModel.components);

  const logger = createLogger();

  await rm(getDistFolder(projectModel.projectFolder), {
    recursive: true,
    force: true,
  }).catch((e: unknown) => {
    throw new CliError({
      code: CliErrorCode.FailedToCleanDistFolder(),
      cause: e,
    });
  });

  const backendBuildOutput = await runBackendBuild(projectModel, logger);

  const browserBuildOutput = await runViteBuild(
    getBuildBrowserViteConfig(projectModel, { customLogger: logger })
  );

  const workerBuildOutput = await runWorkerBuild(projectModel, logger);

  const siteComponentsClientBuildOutput = await runSiteComponentsClientBuild(
    projectModel,
    logger
  );
  const siteComponentsServerBuildOutput = await runSiteComponentsServerBuild(
    projectModel,
    logger
  );
  const siteComponentsSdkBuildOutput = await runSiteComponentsSdkBuild(
    projectModel,
    logger
  );

  const manifest = await createAppManifest({
    appId: projectModel.config.appId,
    components: projectModel.components,
    sentryDSN: projectModel.config.sentryDSN,
    componentSourceBuilder: getComponentBuildSourceBuilder(
      projectModel,
      { browserBuildOutput },
      { workerBuildOutput },
      { backendBuildOutput },
      { siteComponentsClientBuildOutput },
      { siteComponentsServerBuildOutput },
      { siteComponentsSdkBuildOutput }
    ),
  });

  await writeAppManifestFile({
    projectFolder: projectModel.projectFolder,
    manifest,
  });
}
